import { ApplicableList, ListModel, Visibility } from "lib/models/list-models";
import { UserPermissions } from "lib/services";
import { CSRFService } from "lib/services/csrf-service";
import { ListsService } from "lib/services/lists-service";

import { ListsActionTypes, ListType } from "./constants";
import { ListsAction } from "./reducer";

export const getLists =
  (dispatch: React.Dispatch<ListsAction>, listsService: ListsService) =>
  async (options = {}) => {
    dispatch({ type: ListsActionTypes.SET_LOADING, payload: true });
    dispatch({ type: ListsActionTypes.SET_ERROR, payload: null });
    try {
      const response = await listsService.getLists(options);
    } catch (error: unknown) {
      console.error("Error fetching lists:", error);
      dispatch({
        type: ListsActionTypes.SET_ERROR,
        payload: error instanceof Error ? error.message : "Failed to fetch lists",
      });
    } finally {
      dispatch({ type: ListsActionTypes.SET_LOADING, payload: false });
    }
  };

export const getYourLists =
  (dispatch: React.Dispatch<ListsAction>, listsService: ListsService) =>
  async (options = {}) => {
    if (!UserPermissions.current.username) {
      throw new Error("User authentication required to fetch lists");
    }
    dispatch({ type: ListsActionTypes.SET_LOADING, payload: true });
    dispatch({ type: ListsActionTypes.SET_ERROR, payload: null });
    try {
      const response = await listsService.getLists({
        ...options,
        owners: UserPermissions.current.username,
        visibility: Visibility.PRIVATE,
      });
      dispatch({ type: ListsActionTypes.SET_YOUR_LISTS, payload: response.lists });
    } catch (error: unknown) {
      console.error("Error fetching my lists:", error);
      dispatch({
        type: ListsActionTypes.SET_ERROR,
        payload: error instanceof Error ? error.message : "Failed to fetch my lists",
      });
      dispatch({ type: ListsActionTypes.SET_YOUR_LISTS, payload: [] });
    } finally {
      dispatch({ type: ListsActionTypes.SET_LOADING, payload: false });
    }
  };

export const getPublicLists =
  (dispatch: React.Dispatch<ListsAction>, listsService: ListsService) =>
  async (options = {}) => {
    dispatch({ type: ListsActionTypes.SET_LOADING, payload: true });
    dispatch({ type: ListsActionTypes.SET_ERROR, payload: null });
    try {
      const response = await listsService.getLists({ ...options, visibility: Visibility.PUBLIC });
      dispatch({ type: ListsActionTypes.SET_PUBLIC_LISTS, payload: response.lists });
    } catch (error: unknown) {
      console.error("Error fetching public lists:", error);
      dispatch({
        type: ListsActionTypes.SET_ERROR,
        payload: error instanceof Error ? error.message : "Failed to fetch public lists",
      });
      dispatch({ type: ListsActionTypes.SET_PUBLIC_LISTS, payload: [] });
    } finally {
      dispatch({ type: ListsActionTypes.SET_LOADING, payload: false });
    }
  };

export const getSharedLists =
  (dispatch: React.Dispatch<ListsAction>, listsService: ListsService) =>
  async (options = {}) => {
    if (!UserPermissions.current.username) {
      throw new Error("User authentication required to fetch lists");
    }
    dispatch({ type: ListsActionTypes.SET_LOADING, payload: true });
    dispatch({ type: ListsActionTypes.SET_ERROR, payload: null });
    try {
      const response = await listsService.getLists({
        ...options,
        visibility: Visibility.SHARED,
        //includeOwnershipBindleLists: true, Not needed yet
        includeSharedWithBindleLists: true,
      });
      const sharedByMe = response.lists.filter((list) => list.ownerAlias === UserPermissions.current.username);
      dispatch({ type: ListsActionTypes.SET_SHARED_LISTS, payload: sharedByMe });
    } catch (error: unknown) {
      console.error("Error fetching shared lists:", error);
      dispatch({
        type: ListsActionTypes.SET_ERROR,
        payload: error instanceof Error ? error.message : "Failed to fetch shared lists",
      });
      dispatch({ type: ListsActionTypes.SET_SHARED_LISTS, payload: [] });
    } finally {
      dispatch({ type: ListsActionTypes.SET_LOADING, payload: false });
    }
  };

export const getSharedWithYouLists =
  (dispatch: React.Dispatch<ListsAction>, listsService: ListsService) =>
  async (options = {}) => {
    if (!UserPermissions.current.username) {
      throw new Error("User authentication required to fetch lists");
    }
    dispatch({ type: ListsActionTypes.SET_LOADING, payload: true });
    dispatch({ type: ListsActionTypes.SET_ERROR, payload: null });
    try {
      const response = await listsService.getLists({
        ...options,
        visibility: Visibility.SHARED,
        //includeOwnershipBindleLists: false, not needed yet
        includeSharedWithBindleLists: true,
      });
      const sharedWithYou = response.lists.filter((list) => list.ownerAlias !== UserPermissions.current.username);
      dispatch({ type: ListsActionTypes.SET_SHARED_WITH_YOU_LISTS, payload: sharedWithYou });
    } catch (error: unknown) {
      console.error("Error fetching shared with me lists:", error);
      dispatch({
        type: ListsActionTypes.SET_ERROR,
        payload: error instanceof Error ? error.message : "Failed to fetch shared with me lists",
      });
      dispatch({ type: ListsActionTypes.SET_SHARED_WITH_YOU_LISTS, payload: [] });
    } finally {
      dispatch({ type: ListsActionTypes.SET_LOADING, payload: false });
    }
  };

export const evaluateLists =
  (listsService: ListsService, csrfService: CSRFService) =>
  async (
    attributes: Record<string, any>,
    options: { domains?: string[]; includeItemDetails?: boolean; limit?: number } = {}
  ): Promise<ApplicableList[]> => {
    try {
      const csrfToken = await csrfService.getToken();
      const response = await listsService.evaluateLists(attributes, options, csrfToken);
      return response.applicableLists;
    } catch (error: unknown) {
      console.error("Error evaluating lists:", error);
      throw error;
    }
  };

export const createList =
  (dispatch: React.Dispatch<ListsAction>, listsService: ListsService, csrfService: CSRFService) =>
  async (
    data: {
      name: string;
      description?: string;
      visibility?: Visibility;
      allowedDomains?: string[];
      itemIds?: string[];
      evaluationRules?: DocumentType;
      tags?: string[];
    },
    listType: ListType
  ) => {
    if (!UserPermissions.current.username) {
      throw new Error("User authentication required to create lists");
    }

    if (!data.name?.trim()) {
      throw new Error("List name is required");
    }

    if (data.name.length > 255) {
      throw new Error("List name must be 255 characters or less");
    }

    dispatch({ type: ListsActionTypes.SET_ERROR, payload: null });
    if (data.tags?.includes("favorite")) {
      dispatch({ type: ListsActionTypes.SET_CREATING_FAVORITE_LIST, payload: true });
    }
    try {
      const csrfToken = await csrfService.getToken();
      const response = await listsService.createList(
        {
          ...data,
          visibility: data.visibility || Visibility.PRIVATE,
        },
        csrfToken
      );
      dispatch({ type: ListsActionTypes.ADD_LIST, payload: { list: response.list, listType } });
      return response.list;
    } catch (error: unknown) {
      console.error("Error creating list:", error);
      dispatch({
        type: ListsActionTypes.SET_ERROR,
        payload: error instanceof Error ? error.message : "Failed to create list",
      });
      throw error;
    } finally {
      if (data.tags?.includes("favorite")) {
        dispatch({ type: ListsActionTypes.SET_CREATING_FAVORITE_LIST, payload: false });
      }
    }
  };

export const updateList =
  (dispatch: React.Dispatch<ListsAction>, listsService: ListsService, csrfService: CSRFService) =>
  async (
    listId: string,
    data: {
      name: string;
      description?: string;
      visibility?: Visibility;
      allowedDomains?: string[];
      evaluationRules?: DocumentType;
    },
    listType: ListType
  ) => {
    if (!listId?.trim()) {
      throw new Error("List ID is required");
    }

    if (!data.name?.trim()) {
      throw new Error("List name is required");
    }

    if (data.name.length > 255) {
      throw new Error("List name must be 255 characters or less");
    }

    dispatch({ type: ListsActionTypes.SET_ERROR, payload: null });
    try {
      const csrfToken = await csrfService.getToken();
      const response = await listsService.updateList(listId, data, csrfToken);
      dispatch({ type: ListsActionTypes.UPDATE_LIST, payload: { list: response.list, listType } });
      return response.list;
    } catch (error: unknown) {
      console.error("Error updating list:", error);
      dispatch({
        type: ListsActionTypes.SET_ERROR,
        payload: error instanceof Error ? error.message : "Failed to update list",
      });
      throw error;
    }
  };

export const deleteList =
  (dispatch: React.Dispatch<ListsAction>, listsService: ListsService, csrfService: CSRFService) =>
  async (listId: string, listType: ListType) => {
    if (!listId?.trim()) {
      throw new Error("List ID is required");
    }

    dispatch({ type: ListsActionTypes.SET_ERROR, payload: null });
    try {
      const csrfToken = await csrfService.getToken();
      await listsService.deleteList(listId, csrfToken);
      dispatch({ type: ListsActionTypes.DELETE_LIST, payload: { listId, listType } });
      return listId;
    } catch (error: unknown) {
      console.error("Error deleting list:", error);
      dispatch({
        type: ListsActionTypes.SET_ERROR,
        payload: error instanceof Error ? error.message : "Failed to delete list",
      });
      throw error;
    }
  };

export const addItemToList =
  (dispatch: React.Dispatch<ListsAction>, listsService: ListsService, csrfService: CSRFService) =>
  async (
    taxonomyIds: string[],
    options: { listId: string } | { autoCreate: true; name: string; domain: string },
    listType: ListType
  ) => {
    if (!taxonomyIds || taxonomyIds.length === 0) {
      throw new Error("At least one item ID is required");
    }

    if ("listId" in options && !options.listId?.trim()) {
      throw new Error("List ID is required");
    }

    if ("autoCreate" in options && !options.name?.trim()) {
      throw new Error("List name is required for auto-create");
    }

    dispatch({ type: ListsActionTypes.SET_ERROR, payload: null });
    try {
      const csrfToken = await csrfService.getToken();
      const response = await listsService.addItemToList({ itemIds: taxonomyIds, ...options }, csrfToken);
      if (response.success) {
        const listResponse = await listsService.getList(response.listId, true);
        dispatch({ type: ListsActionTypes.UPDATE_LIST, payload: { list: listResponse.list, listType } });
        return listResponse.list;
      }
      throw new Error("Failed to add item to list");
    } catch (error: unknown) {
      console.error("Error adding item to list:", error);
      dispatch({
        type: ListsActionTypes.SET_ERROR,
        payload: error instanceof Error ? error.message : "Failed to add item to list",
      });
      throw error;
    }
  };

export const removeItemFromList =
  (dispatch: React.Dispatch<ListsAction>, listsService: ListsService, csrfService: CSRFService) =>
  async (listId: string, itemId: string, listType: ListType) => {
    if (!listId?.trim()) {
      throw new Error("List ID is required");
    }

    if (!itemId?.trim()) {
      throw new Error("Item ID is required");
    }

    dispatch({ type: ListsActionTypes.SET_ERROR, payload: null });
    try {
      const csrfToken = await csrfService.getToken();
      await listsService.removeItemFromList(listId, itemId, csrfToken);
      dispatch({ type: ListsActionTypes.UPDATE_LIST_ITEMS, payload: { listId, itemId, listType } });
      return { listId, itemId };
    } catch (error: unknown) {
      console.error("Error removing item from list:", error);
      dispatch({
        type: ListsActionTypes.SET_ERROR,
        payload: error instanceof Error ? error.message : "Failed to remove item",
      });
      throw error;
    }
  };

export const getListById =
  (listsService: ListsService) =>
  async (listId: string, includeItemDetails: boolean = true): Promise<ListModel> => {
    try {
      const response = await listsService.getList(listId, includeItemDetails);
      return response.list;
    } catch (error: unknown) {
      console.error("Error fetching list by id:", error);
      throw error;
    }
  };
